import { cx } from 'class-variance-authority';
import { type To } from 'react-router-dom';

import { Icon } from '../../common/Icons';
import type { SemanticIconType } from '../../common/Icons/SemanticIcons';
import { NavLink } from '../NavLink';

type NavLinkProps = {
  text: string;
  to: To;
  icon?: SemanticIconType;
  showLabel?: boolean;
};

/**
 * A NavbarLink component that renders a link in the navbar.
 * Icon stays in fixed position regardless of expanded/collapsed state.
 */
export const NavbarLink = ({ text, icon, to, showLabel = true }: NavLinkProps) => {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        cx(
          // Fixed layout - icon always in same position, slightly rounded
          'relative flex items-center gap-3 rounded h-10 px-3 transition-colors text-sm',
          // Default state
          'text-neutral-600',
          // Hover state
          'hover:bg-neutral-100 hover:text-neutral-900',
          // Focus state
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-1',
          // Active state
          isActive && 'bg-blue-50 text-blue-700 font-medium',
        )
      }
      aria-label={text}
    >
      {/* Icon container - fixed size to maintain position */}
      <div className="w-5 h-5 flex items-center justify-center shrink-0">
      {icon && <Icon name={icon} className="h-5 w-5 transition-colors" />}
      </div>

      {/* Label - animates width and opacity */}
      <span
        className="whitespace-nowrap overflow-hidden transition-all duration-200"
        style={{
          opacity: showLabel ? 1 : 0,
          width: showLabel ? 'auto' : 0,
        }}
      >
      {text}
      </span>
    </NavLink>
  );
};
